import React from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import cn from 'classnames';
import IconGenerateReportButton from 'assets/iconGenerateReportButton';
import {
  InvoiceDocumentButton,
  InvoiceDocumentsCell
} from 'routes/App/reports/ReportsTable/styles';
import {
  DOWNLOAD_SELF_DELIVERY_INVOICE_ORDERS,
  OPEN_SELF_DELIVERY_INVOICE
} from 'store/reports/action';
import {POSTHOG_EVENT_NAMES} from 'helpers/constants/reports';

const IconInvoice = () => (
  <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z'
      stroke='#FF2D38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M14 3v5h5M9 13h6M9 17h6'
      stroke='#FF2D38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const IconDownloadSmall = () => (
  <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2'
      stroke='#FF2D38'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const SelfDeliveryInvoiceActions = ({row}) => {
  const dispatch = useDispatch();
  const {
    invoiceOrdersDownloading: downloading,
    invoiceLinkLoading
  } = useSelector((state) => state.reportsReducer);
  const {activeLocation, userData} = useSelector((state) => state.user);
  // A stripe number can appear on two rows (two charge dates); key the spinners per row.
  const rowKey = `${row.stripeNumber}|${row.chargeDate}`;
  const isDownloading = downloading === rowKey;
  const isOpeningInvoice = invoiceLinkLoading === rowKey;

  const track = (eventName) => {
    window?.posthog?.capture(eventName, {
      stripe_number: row.stripeNumber,
      charge_date: row.chargeDate,
      location_id: activeLocation?._id,
      location_name: activeLocation?.BusinessName || activeLocation?.FullName,
      user_id: userData?._id,
      user_email: userData?.email,
      timestamp: moment().utc().format()
    });
  };

  return (
    <InvoiceDocumentsCell>
      <InvoiceDocumentButton
        type='button'
        className={cn({loading: isOpeningInvoice})}
        disabled={Boolean(invoiceLinkLoading)}
        onClick={() => {
          track(POSTHOG_EVENT_NAMES.SELF_DELIVERY_INVOICE_VIEW_CLICKED);
          dispatch(
            OPEN_SELF_DELIVERY_INVOICE({
              stripeNumber: row.stripeNumber,
              rowKey
            })
          );
        }}
      >
        {isOpeningInvoice ? <IconGenerateReportButton /> : <IconInvoice />}
        View Invoice
      </InvoiceDocumentButton>
      <InvoiceDocumentButton
        type='button'
        className={cn({loading: isDownloading})}
        disabled={Boolean(downloading)}
        onClick={() => {
          track(
            POSTHOG_EVENT_NAMES.SELF_DELIVERY_INVOICE_ORDER_BREAKDOWN_CLICKED
          );
          dispatch(
            DOWNLOAD_SELF_DELIVERY_INVOICE_ORDERS({
              stripeNumber: row.stripeNumber,
              chargeDate: row.chargeDate,
              rowKey
            })
          );
        }}
      >
        {isDownloading ? <IconGenerateReportButton /> : <IconDownloadSmall />}
        Order Breakdown
      </InvoiceDocumentButton>
    </InvoiceDocumentsCell>
  );
};

SelfDeliveryInvoiceActions.propTypes = {
  row: PropTypes.shape({
    stripeNumber: PropTypes.string.isRequired,
    chargeDate: PropTypes.string.isRequired
  }).isRequired
};

export default SelfDeliveryInvoiceActions;
